import { IECTDMetadata, IStructuredDocument } from "models/assets.models";
import {
  IDocument,
  IImportedHAQMetadata,
  IProjectParticipantsMetadata,
  ISupportingInformationMetadata,
  TDocumentMetadata,
  TECTDOrDocumentMetadata,
} from "models/documents.models";
import { IOrganization } from "models/organizations.models";
import { THAQParentData } from "screens/Project/components/HAQ";
import {
  BASE_DOCUMENT_SUBTYPES,
  DOCUMENT_TYPES_FOR_DOWNLOAD,
  DOCUMENT_TYPES_FOR_PREVIEW,
  DocumentStatus,
  DocumentSubtype,
} from "utils/constants/doc.constants";
import { TPossibleAsset } from "utils/types";
import { isHAUser } from "utils/user/organization";

export const isDocumentTypeSupportedForDownload = (docType?: string) =>
  DOCUMENT_TYPES_FOR_DOWNLOAD.some((type) => type === docType);

export const isDocumentReadyForSending = (
  status: DocumentStatus,
  organization: IOrganization | undefined,
) =>
  [DocumentStatus.Final, DocumentStatus.Cleared].includes(status) &&
  isHAUser(organization);

export const isDocumentPendingUpload = (document: IDocument) =>
  [DocumentStatus.UploadInitiated, DocumentStatus.UploadProcessing].includes(
    document.status,
  );

export const isDocumentFailedToUpload = (document: IDocument) =>
  [DocumentStatus.UploadFailed].includes(document.status);

export const isDocumentTypeSupportedForPreview = (docType?: string) =>
  DOCUMENT_TYPES_FOR_PREVIEW.some((type) => type === docType);

export const isStructuredDocumentCreated = (documents?: IDocument[]) =>
  documents?.some(
    (document) => document.type === DocumentSubtype.StructuredDocument,
  );

export const isStructuredDocument = (
  document: TPossibleAsset,
): document is IStructuredDocument => {
  return document?.type === DocumentSubtype.StructuredDocument;
};

export const isStructuredDocumentChild = (
  document: TPossibleAsset | THAQParentData | undefined,
): document is IDocument =>
  (document as IDocument)?.type === DocumentSubtype.StructuredDocumentChild;

export const isECTD = (
  document: IDocument | null | undefined,
): document is IDocument<IECTDMetadata> =>
  document?.type === DocumentSubtype.ECTD;

export const isSupportingInformationDocument = (
  document: IDocument | null | undefined,
): document is IDocument<ISupportingInformationMetadata> =>
  !!(
    document && [DocumentSubtype.SupportingInformation].includes(document.type)
  );

export const isBaseDocument = (
  document: IDocument | null | undefined,
): document is IDocument<TDocumentMetadata> =>
  !!(document && BASE_DOCUMENT_SUBTYPES.includes(document.type));

export const isECTDOrBaseDocument = (
  document: IDocument | null | undefined,
): document is IDocument<TECTDOrDocumentMetadata> =>
  !!(
    document &&
    [...BASE_DOCUMENT_SUBTYPES, DocumentSubtype.ECTD].includes(document.type)
  );

export const isDocumentWithInformationData = (
  document: IDocument | null | undefined,
): document is IDocument<
  TECTDOrDocumentMetadata | IImportedHAQMetadata | IProjectParticipantsMetadata
> => !!(document && !isStructuredDocument(document));
